/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {
  ContextServiceConfigurationApiService,
  ContextServiceConfigurationModel
} from './context-service-configuration-api.service';
import {ContextServiceConfigurationEditPageComponent} from './context-service-configuration-edit-page.component';
import {ContextServiceConfigurationListPageComponent} from './context-service-configuration-list-page.component';

export function contextServiceConfigurationStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ContextServiceConfigurationApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const rh = adnRouterHelper.createRouterHelper(
    'context-service-configurations.context-service-configuration',
    ContextServiceConfigurationApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA']},
    resolve: {listReadOnly: () => false},
    views: {
      'content@app': {
        component: ContextServiceConfigurationListPageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      contextServiceConfigurationPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: ContextServiceConfigurationModel = {
            id: $stateParams.id,
            objectState: 'ACTIVE',
            validationWarnings: []
          };
          return new SignalAdminResourcePageState(
            model,
            true,
            false,
            api,
            'Context Service Configuration'
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model,
          false,
          false,
          api,
          'Context Service Configuration'
        ));
      },
      model: function(
        contextServiceConfigurationPage: SignalAdminResourcePageState<ContextServiceConfigurationModel>
      ) {
        return contextServiceConfigurationPage.model();
      }
    },
    views: {
      'content@app': {
        component: ContextServiceConfigurationEditPageComponent,
        bindings: {state: 'contextServiceConfigurationPage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'contextServiceConfigurationPage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'contextServiceConfigurationPage'}
      }
    }
  }));

  return states.build();
}
